// Mobile menu: inside a section of the slide-out menu, each sub-heading that has its own
// links (e.g. Homeware > Japanese Incense) shows them straight away underneath it, as a
// tight list of plain text links, instead of hiding them behind a "+" toggle. The
// sub-heading itself links to the whole category (the menu's "All" link for it), so "All"
// isn't repeated.
// In a section that has such groups, the section's first line (when it's a plain link, such
// as "All" or Popular Collections' "Newest Arrivals") stays full width at the top; everything
// after it (groups and plain links like Ceramics or Vases alike) is laid out in two columns,
// with a very faint line between them:
// - the left column is always at least as long as the right, and the columns are as even
//   as possible (measured from the entries' real heights);
// - menu order is kept where it can be; an entry only moves to the other column when that
//   makes the columns clearly more even. Each column lists its entries in menu order.
// - Alphabetical sections (Makers: A–I, J–L…) always keep their order, with the most even
//   split.
// Sections with only plain links are left as a simple list.
// One even rhythm: every main line is the same height (long names wrap tightly), every
// sub-link the same smaller height, and every group with sub-links ends with the same gap.
// Lines are spaced so each link is comfortable to tap, and each link's tap area spans the
// full width of its column.
// Links are tappable the moment the menu opens: the theme's delayed slide-in (during which
// links can't be tapped) is replaced by a quick fade.
//
// The menu is built while it's closed: on load, and again whenever the theme re-renders the
// header (it does this shortly after load). Columns are only ever re-balanced while their
// section is out of sight, so nothing moves under a customer's finger.
// Loaded from snippets/menu-curated-collections.liquid.

const STYLE_ID = 'mujo-menu-chips-css';
const MOVE_COST = 40; // px of unevenness worth one entry moving out of menu order

const CSS = `
@keyframes mujo-nav-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
.menu-drawer-container[open] .menu-drawer__animated-element {
  animation: mujo-nav-in 0.18s ease-out both !important;
  visibility: visible;
}
@media (prefers-reduced-motion: reduce) {
  .menu-drawer-container[open] .menu-drawer__animated-element { animation: none !important; }
}
.menu-drawer__menu--childlist:has([data-mujo-chips]) {
  --mujo-line-pad: 11px;
  --mujo-sub-pad: 7px;
  --mujo-group-end: 14px;
  --mujo-col-gap: 14px;
}
.menu-drawer__menu--childlist:has([data-mujo-chips]) li {
  min-height: 0;
}
.menu-drawer__menu--childlist:has([data-mujo-chips]) li > .menu-drawer__menu-item {
  min-height: 0;
  padding-block: var(--mujo-line-pad);
  line-height: 1.3;
}
.mujo-groups {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 0;
  align-items: stretch;
}
.mujo-groups__col {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}
.mujo-groups__col:first-child {
  padding-inline-end: var(--mujo-col-gap);
}
.mujo-groups__col:last-child {
  padding-inline-start: var(--mujo-col-gap);
  border-inline-start: 1px solid color-mix(in srgb, var(--color-foreground) 8%, transparent);
  margin-block: var(--mujo-line-pad) var(--mujo-group-end);
  padding-block: 0;
}
.mujo-groups__col:last-child > li:first-child > .menu-drawer__menu-item {
  padding-block-start: 0;
}
.mujo-groups__col > li {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
}
.menu-drawer__list-item--deep[data-mujo-chips] {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
  padding-block-end: var(--mujo-group-end);
}
.mujo-groups__col:last-child > li:last-child[data-mujo-chips] {
  padding-block-end: 0;
}
.menu-drawer__list-item--deep[data-mujo-chips] > accordion-custom { display: none; }
.mujo-chips__heading {
  margin: 0;
  text-decoration: none;
  color: inherit;
}
.mujo-chips {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-block-start: calc(-1 * var(--mujo-line-pad) + var(--mujo-sub-pad) - 4px);
}
.mujo-chip {
  display: block;
  padding-block: var(--mujo-sub-pad);
  font-size: 14px;
  line-height: 1.3;
  color: color-mix(in srgb, var(--color-foreground) 68%, transparent);
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
}
.mujo-chip:hover,
.mujo-chip:active { color: var(--color-foreground); }
.mujo-chip[aria-current='page'] {
  color: var(--color-foreground);
  text-decoration: underline;
  text-underline-offset: 0.25em;
  text-decoration-thickness: 1px;
}
`;

const ensureStyles = () => {
  const existing = document.getElementById(STYLE_ID);
  if (existing) {
    if (existing.textContent !== CSS) existing.textContent = CSS;
    return;
  }
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.append(style);
};

const path = (href) => {
  try {
    return new URL(href, window.location.origin).pathname.replace(/\/$/, '');
  } catch {
    return href;
  }
};

const isAllLink = (a) => /^(all|shop all|view all|see all)$/i.test(a.textContent.trim());

/** Turn one "+" group into a heading with its links listed underneath. */
const buildGroup = (li, here) => {
  const details = li.querySelector('details');
  const summary = details?.querySelector('summary');
  const list = details?.querySelector('.menu-drawer__menu--grandchildlist');
  if (!details || !summary || !list) return false;

  const title = summary.querySelector('.menu-drawer__menu-item-text')?.textContent.trim() || '';
  const links = [...list.querySelectorAll('a[href]')];
  const allLink = links.find(isAllLink);

  // Sub-heading, styled like the section's other links; it opens the whole category when
  // the menu has an "All" link for it
  const heading = document.createElement(allLink ? 'a' : 'p');
  heading.className = 'menu-drawer__menu-item menu-drawer__menu-item--parent focus-inset mujo-chips__heading';
  if (allLink) heading.href = allLink.getAttribute('href');
  const text = document.createElement('span');
  text.className = 'menu-drawer__menu-item-text';
  text.textContent = title;
  heading.append(text);

  const chips = document.createElement('div');
  chips.className = 'mujo-chips';
  chips.style.fontFamily = getComputedStyle(summary).fontFamily;
  let count = 0;
  for (const link of links) {
    if (link === allLink) continue;
    const chip = document.createElement('a');
    chip.className = 'mujo-chip';
    chip.href = link.getAttribute('href');
    chip.textContent = link.textContent.trim();
    if (path(chip.href) === here) chip.setAttribute('aria-current', 'page');
    chips.append(chip);
    count++;
  }

  li.dataset.mujoChips = String(count);
  li.append(heading, chips);
  return true;
};

/** Rough height of an entry (in px) when it can't be measured. */
const estimate = (li) => {
  const lines = (text, perLine) => Math.max(1, Math.ceil(text.length / perLine));
  const heading = li.querySelector(':scope > .menu-drawer__menu-item');
  let px = 43 * lines(heading?.textContent.trim() || '', 17);
  li.querySelectorAll('.mujo-chip').forEach((chip) => {
    px += 32 * lines(chip.textContent.trim(), 21);
  });
  if (li.dataset.mujoChips !== undefined) px += 14;
  return px;
};

const headingText = (li) => li.querySelector(':scope > .menu-drawer__menu-item')?.textContent.trim() || '';

/** Which entries go in the left column (true) / right column (false). */
const chooseColumns = (heights, keepOrder) => {
  const n = heights.length;
  const total = heights.reduce((a, b) => a + b, 0);

  // Alphabetical sections: keep the order, most even split
  if (keepOrder || n > 16) {
    let best = 1;
    let bestScore = Infinity;
    let running = 0;
    for (let k = 1; k < n; k++) {
      running += heights[k - 1];
      const diff = Math.abs(total - 2 * running);
      // Prefer the left being longer when two splits are about as even
      const score = diff + (running < total - running ? 1 : 0);
      if (score < bestScore) {
        bestScore = score;
        best = k;
      }
    }
    return heights.map((_, i) => i < best);
  }

  // Every way of dividing the entries: left at least as long as the right, as even as
  // possible, with a cost for each entry that ends up out of menu order
  let bestMask = 0;
  let bestCost = Infinity;
  for (let mask = 1; mask < 1 << n; mask++) {
    let left = 0;
    for (let i = 0; i < n; i++) if (mask & (1 << i)) left += heights[i];
    const right = total - left;
    if (left < right || right === 0) continue;
    // Out of order: an earlier entry on the right while a later one is on the left
    let moves = 0;
    let rightSoFar = 0;
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) moves += rightSoFar;
      else rightSoFar++;
    }
    const cost = left - right + moves * MOVE_COST;
    if (cost < bestCost) {
      bestCost = cost;
      bestMask = mask;
    }
  }
  if (bestCost === Infinity) return heights.map((_, i) => i < n - 1);
  return heights.map((_, i) => Boolean(bestMask & (1 << i)));
};

/** Is this section of the menu on screen (open, or sliding in/out)? */
const isShowing = (wrapper) => {
  const submenu = wrapper.closest('.menu-drawer__submenu');
  const drawer = wrapper.closest('.menu-drawer');
  if (!submenu) return false;
  if (getComputedStyle(submenu).visibility === 'hidden') return false;
  const rect = submenu.getBoundingClientRect();
  const drawerRect = drawer?.getBoundingClientRect();
  if (!rect.width) return false;
  if (drawerRect && (rect.right <= drawerRect.left || rect.left >= drawerRect.right)) return false;
  return true;
};

/** Place a section's entries in its two columns using their current heights. */
const balance = (wrapper, { force = false } = {}) => {
  const [left, right] = wrapper.querySelectorAll(':scope > .mujo-groups__col');
  if (!left || !right) return;
  // Never move links around while the customer can see (and may be tapping) them
  if (!force && isShowing(wrapper)) {
    wrapper.dataset.mujoStale = '';
    return;
  }
  const entries = [...wrapper.querySelectorAll(':scope > .mujo-groups__col > li')].sort(
    (a, b) => Number(a.dataset.mujoIndex) - Number(b.dataset.mujoIndex)
  );
  let heights = entries.map((li) => li.getBoundingClientRect().height);
  if (heights.some((h) => !h)) heights = entries.map(estimate);
  const signature = heights.map(Math.round).join(',');
  delete wrapper.dataset.mujoStale;
  if (wrapper.dataset.mujoSignature === signature) return;
  wrapper.dataset.mujoSignature = signature;

  const alphabetical = entries.every((li) => /^[a-z0-9]\s*[-–]\s*[a-z0-9]$/i.test(headingText(li)));
  const inLeft = chooseColumns(heights, alphabetical);
  entries.forEach((li, i) => (inLeft[i] ? left : right).append(li));
};

// Re-balance when an entry changes size (e.g. fonts finishing loading), but only while
// that section is out of sight
let pending = new Set();
let scheduled = false;
const resizeObserver =
  'ResizeObserver' in window
    ? new ResizeObserver((records) => {
        for (const record of records) {
          const wrapper = record.target.closest('.mujo-groups');
          if (wrapper) pending.add(wrapper);
        }
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          const wrappers = pending;
          pending = new Set();
          wrappers.forEach((wrapper) => balance(wrapper));
        });
      })
    : null;

/** Gather a section's entries (after its first, full-width line) into a two-column block. */
const arrangeColumns = (ul) => {
  if (ul.dataset.mujoArranged !== undefined) return;
  const children = [...ul.children];
  const first = children[0];
  const leadIsPlainLink =
    first && first.dataset.mujoChips === undefined && first.querySelector(':scope > a.menu-drawer__menu-item');
  const entries = children.filter((li, i) => {
    if (i === 0 && leadIsPlainLink) return false; // "All" / "Newest Arrivals" stays on top
    if (li.dataset.mujoChips !== undefined) return true;
    const link = li.querySelector(':scope > a.menu-drawer__menu-item');
    return link && !isAllLink(link);
  });
  if (!entries.some((li) => li.dataset.mujoChips !== undefined) || entries.length < 2) return;
  ul.dataset.mujoArranged = '';

  const wrapper = document.createElement('li');
  wrapper.className = 'menu-drawer__list-item mujo-groups';
  const left = document.createElement('ul');
  const right = document.createElement('ul');
  left.className = right.className = 'mujo-groups__col';
  left.setAttribute('role', 'list');
  right.setAttribute('role', 'list');
  wrapper.append(left, right);
  entries[0].before(wrapper);
  entries.forEach((li, i) => {
    li.dataset.mujoIndex = String(i);
    left.append(li);
    resizeObserver?.observe(li);
  });
  balance(wrapper, { force: true });
};

/** Build any menu sections that haven't been built yet. */
const mountChips = () => {
  const items = document.querySelectorAll('.menu-drawer .menu-drawer__list-item--deep:not([data-mujo-chips])');
  if (!items.length) return;
  ensureStyles();
  const here = path(window.location.href);
  const lists = new Set();
  items.forEach((li) => {
    if (buildGroup(li, here)) {
      const ul = li.closest('.menu-drawer__menu--childlist');
      if (ul) lists.add(ul);
    }
  });
  lists.forEach(arrangeColumns);
};

/** Catch up on any re-balancing that was put off while a section was showing. */
const settleHidden = () => {
  document.querySelectorAll('.menu-drawer .mujo-groups[data-mujo-stale]').forEach((wrapper) => balance(wrapper));
};

if (!window.__mujoMenuChips) {
  window.__mujoMenuChips = true;
  ensureStyles();
  mountChips();

  // Rebuild straight away if the theme re-renders the header (new, unbuilt menu)
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      mountChips();
    });
  }).observe(document.body, { childList: true, subtree: true });

  // Backstop: build on a touch outside the open menu (e.g. the menu button) - never on a
  // touch inside it, so nothing moves under the customer's finger
  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.target instanceof Element && event.target.closest('.menu-drawer')) return;
      mountChips();
    },
    { capture: true, passive: true }
  );
  document.fonts?.ready.then(() => {
    mountChips();
    document.querySelectorAll('.menu-drawer .mujo-groups').forEach((wrapper) => balance(wrapper));
  });
  // After a section has slid away, apply any re-balancing that was waiting
  document.addEventListener('transitionend', settleHidden, { passive: true });
}